import { useState, useEffect } from 'react';
import { selectedBot } from '../../../lib/systemScope.ts';
import { useAuth } from '../../../contexts/AuthContext';
import { useBot } from '../../../contexts/BotContext';
import {
  hubService,
  type GenericIntent,
  type CreateGenericIntentDto,
  type GenericIntentSyncResult,
  type BotGenericIntentConfig,
} from '../../../services/hub.service';
import { AiConfigTabShell, AiConfigTable } from '../index';

/**
 * Generic intents power two detection tiers:
 *  - triggerPatterns → L0 regex floor (exact keywords, instant, offline-safe)
 *  - examplePhrases  → L1 semantic examples, synced to Qdrant and confirmed
 *    by the LLM verifier before acting
 * Phrase edits are NOT searchable until synced — hence the sync button and
 * the "unsynced changes" banner.
 */

const FIELD_HELP = {
  patterns:
    'L0 regex floor — exact keywords, matched instantly (Unicode word boundaries; \\b is safe for Cyrillic). One pattern per line.',
  phrases:
    'L1 semantic examples — paraphrases in any language. Embedded into Qdrant and confirmed by the LLM verifier. One phrase per line. Requires "Sync to Qdrant" after editing.',
};

const splitLines = (v: string) =>
  v.split('\n').map((s) => s.trim()).filter(Boolean);

export function GenericIntentsTab() {
  const { getToken } = useAuth();
  const { system, botId } = useBot();
  // A1: overrides are per (system, botId) — none are shown or written until a bot is chosen.
  const bot = selectedBot({ system, botId });
  const [items, setItems] = useState<GenericIntent[]>([]);
  const [botConfigs, setBotConfigs] = useState<BotGenericIntentConfig[]>([]);
  const [botCfgError, setBotCfgError] = useState<string | null>(null);
  const [editingBotCfg, setEditingBotCfg] = useState<BotGenericIntentConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<GenericIntent | null>(null);
  const [dirtySinceSync, setDirtySinceSync] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<GenericIntentSyncResult | null>(null);
  const [syncedAt, setSyncedAt] = useState<Date | null>(null);
  const [form, setForm] = useState<CreateGenericIntentDto>({
    code: '',
    name: '',
    description: '',
    isActive: true,
    triggerPatterns: [],
    examplePhrases: [],
  });

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    loadBotConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [system, botId]);

  async function loadBotConfigs() {
    setBotCfgError(null);
    if (!bot) {
      setBotConfigs([]);
      return;
    }
    try {
      const token = getToken();
      setBotConfigs(await hubService.listBotGenericIntentConfigs(bot, token));
    } catch (e) {
      setBotCfgError(e instanceof Error ? e.message : 'Failed to load bot overrides');
    }
  }

  async function handleSaveBotCfg() {
    if (!editingBotCfg || !bot) return;
    try {
      const token = getToken();
      await hubService.upsertBotGenericIntentConfig(
        {
          system: bot.system,
          botId: bot.botId,
          code: editingBotCfg.code,
          enabled: editingBotCfg.enabled,
          // null (not undefined) so clearing persists — same convention as
          // defaultReply above.
          replyConfig: editingBotCfg.replyConfig?.mn ? editingBotCfg.replyConfig : null,
          blockRef: editingBotCfg.blockRef || null,
          workflowId: editingBotCfg.workflowId || null,
        },
        token,
      );
      setEditingBotCfg(null);
      loadBotConfigs();
    } catch (e) {
      setBotCfgError(e instanceof Error ? e.message : 'Save failed');
    }
  }

  async function handleDeleteBotCfg(code: string) {
    if (!bot) return;
    if (!confirm(`Remove bot ${botId}'s override for "${code}"? The platform default applies again.`)) return;
    try {
      const token = getToken();
      await hubService.deleteBotGenericIntentConfig(bot, code, token);
      loadBotConfigs();
    } catch (e) {
      setBotCfgError(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const data = await hubService.listGenericIntents(true, token);
      setItems(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  async function handleSync() {
    setSyncing(true);
    setError(null);
    try {
      const token = getToken();
      const result = await hubService.syncGenericIntents(token);
      setSyncResult(result);
      setSyncedAt(new Date());
      setDirtySinceSync(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  }

  async function handleCreate() {
    if (!form.code.trim() || !form.name.trim()) return;
    try {
      const token = getToken();
      await hubService.createGenericIntent(
        {
          ...form,
          triggerPatterns: form.triggerPatterns?.filter(Boolean),
          examplePhrases: form.examplePhrases?.filter(Boolean),
        },
        token,
      );
      setCreating(false);
      setForm({ code: '', name: '', description: '', isActive: true, triggerPatterns: [], examplePhrases: [] });
      setDirtySinceSync(true);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    }
  }

  async function handleUpdate(code: string) {
    if (!editing) return;
    try {
      const token = getToken();
      await hubService.updateGenericIntent(
        code,
        {
          name: editing.name,
          description: editing.description,
          isActive: editing.isActive,
          triggerPatterns: (editing.triggerPatterns ?? []).filter(Boolean),
          examplePhrases: (editing.examplePhrases ?? []).filter(Boolean),
          // null (not undefined) so clearing the field actually persists —
          // JSON.stringify drops undefined keys and the old value would stick.
          defaultReply: editing.defaultReply ?? null,
        },
        token,
      );
      setEditing(null);
      setDirtySinceSync(true);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    }
  }

  async function handleDelete(code: string) {
    if (!confirm(`Delete generic intent "${code}"?`)) return;
    try {
      const token = getToken();
      await hubService.deleteGenericIntent(code, token);
      setDirtySinceSync(true);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  const syncBadge = (code: string) => {
    const r = syncResult?.results.find((x) => x.code === code);
    if (!r) return null;
    return r.ok ? (
      <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs text-green-700">
        ✓ {r.upserted ?? 0} synced{r.removedStale ? `, ${r.removedStale} removed` : ''}
      </span>
    ) : (
      <span
        className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-xs text-red-700"
        title={r.error}
      >
        ✗ failed
      </span>
    );
  };

  return (
    <AiConfigTabShell loading={loading} error={error}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          {!creating && (
            <button
              onClick={() => setCreating(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
            >
              Add Generic Intent
            </button>
          )}
          <button
            onClick={handleSync}
            disabled={syncing}
            className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md disabled:opacity-50"
          >
            {syncing ? 'Syncing…' : 'Sync to Qdrant'}
          </button>
          {syncedAt && !dirtySinceSync && (
            <span className="text-xs text-gray-500">
              Last sync {syncedAt.toLocaleTimeString()} — {syncResult?.synced ?? 0} intents,{' '}
              {syncResult?.results.reduce((s, r) => s + (r.upserted ?? 0), 0)} phrases upserted
              {syncResult?.results.some((r) => !r.ok) && (
                <span className="text-red-600"> — some failed, see badges</span>
              )}
            </span>
          )}
        </div>

        {dirtySinceSync && (
          <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-2 text-sm text-amber-800">
            Phrases changed since the last sync — they are not searchable until
            you run <b>Sync to Qdrant</b>.
          </div>
        )}

        {creating && (
          <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-3">
            <input
              placeholder="Code (e.g. greeting)"
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
            <input
              placeholder="Name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
            <textarea
              placeholder="Description (shown to the LLM verifier as context)"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              rows={2}
            />
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Trigger patterns (one per line)</label>
              <p className="text-xs text-gray-400 mb-1">{FIELD_HELP.patterns}</p>
              <textarea
                placeholder={'\\b(hello|hi)\\b\n\\b(сайн уу)\\b'}
                value={(form.triggerPatterns || []).join('\n')}
                onChange={(e) => setForm((f) => ({ ...f, triggerPatterns: splitLines(e.target.value) }))}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm font-mono"
                rows={3}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Example phrases (one per line)</label>
              <p className="text-xs text-gray-400 mb-1">{FIELD_HELP.phrases}</p>
              <textarea
                placeholder={'сайн байна уу\nsain bna u\nhello there'}
                value={(form.examplePhrases || []).join('\n')}
                onChange={(e) => setForm((f) => ({ ...f, examplePhrases: splitLines(e.target.value) }))}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                rows={4}
              />
            </div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.isActive !== false}
                onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
              />
              Active
            </label>
            <div className="flex gap-2">
              <button onClick={handleCreate} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded">
                Save
              </button>
              <button onClick={() => setCreating(false)} className="px-3 py-1.5 text-sm bg-gray-200 rounded">
                Cancel
              </button>
            </div>
          </div>
        )}

        <AiConfigTable>
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Patterns (L0)</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Phrases (L1)</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Active</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Qdrant</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {items.map((item) => (
              <tr key={item.code} className={item.isActive === false ? 'opacity-50' : ''}>
                <td className="px-4 py-2 text-sm font-mono">{item.code}</td>
                <td className="px-4 py-2 text-sm">{item.name}</td>
                <td className="px-4 py-2 text-sm text-gray-500">{(item.triggerPatterns ?? []).length}</td>
                <td className="px-4 py-2 text-sm text-gray-500">{(item.examplePhrases ?? []).length}</td>
                <td className="px-4 py-2 text-sm">{item.isActive !== false ? 'Yes' : 'No'}</td>
                <td className="px-4 py-2 text-sm">{syncBadge(item.code) ?? <span className="text-gray-300">—</span>}</td>
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={() => setEditing(item)}
                    className="text-blue-600 hover:underline text-sm mr-2"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(item.code)}
                    className="text-red-600 hover:underline text-sm"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </AiConfigTable>

        <div className="pt-6 space-y-3">
          <div>
            <h3 className="font-semibold text-sm">
              Bot overrides — {bot ? <span className="font-mono">{bot.system}/{bot.botId}</span> : 'no bot selected'}
            </h3>
            {!bot && <p className="text-xs text-amber-700">Choose a bot in the top bar to see and edit its overrides.</p>}
            <p className="text-xs text-gray-400">
              Cascade: bot override → platform default reply → nothing (RAG).
              Overrides here beat the default reply for this bot only.
            </p>
          </div>
          {botCfgError && (
            <div className="rounded-md bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-700">
              {botCfgError}
            </div>
          )}
          <AiConfigTable>
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Effective reply</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Enabled</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {items.map((gi) => {
                const cfg = botConfigs.find((c) => c.code === gi.code);
                const effective = cfg?.blockRef
                  ? `block: ${cfg.blockRef}`
                  : cfg?.workflowId
                    ? `workflow: ${cfg.workflowId}`
                    : cfg?.replyConfig?.mn ?? gi.defaultReply?.mn ?? '—';
                return (
                  <tr key={gi.code} className={cfg?.enabled === false ? 'opacity-50' : ''}>
                    <td className="px-4 py-2 text-sm font-mono">{gi.code}</td>
                    <td className="px-4 py-2 text-sm max-w-md truncate" title={effective}>{effective}</td>
                    <td className="px-4 py-2 text-sm text-gray-500">
                      {cfg ? 'bot override' : gi.defaultReply?.mn ? 'platform default' : '—'}
                    </td>
                    <td className="px-4 py-2 text-sm">{cfg?.enabled === false ? 'No (silenced)' : 'Yes'}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() =>
                          setEditingBotCfg(
                            cfg ?? { botId, code: gi.code, enabled: true, replyConfig: null, blockRef: null, workflowId: null },
                          )
                        }
                        className="text-blue-600 hover:underline text-sm mr-2"
                      >
                        {cfg ? 'Edit override' : 'Override'}
                      </button>
                      {cfg && (
                        <button
                          onClick={() => handleDeleteBotCfg(gi.code)}
                          className="text-red-600 hover:underline text-sm"
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </AiConfigTable>
        </div>

        {editingBotCfg && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg max-w-lg w-full space-y-3 max-h-[90vh] overflow-y-auto">
              <h3 className="font-semibold">
                {editingBotCfg.code} — bot <span className="font-mono">{botId}</span> override
              </h3>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={editingBotCfg.enabled !== false}
                  onChange={(e) =>
                    setEditingBotCfg((x) => (x ? { ...x, enabled: e.target.checked } : null))
                  }
                />
                Enabled (off = this bot stays silent for this intent)
              </label>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Reply override (Монгол)</label>
                <textarea
                  value={editingBotCfg.replyConfig?.mn ?? ''}
                  onChange={(e) =>
                    setEditingBotCfg((x) =>
                      x
                        ? {
                            ...x,
                            replyConfig: e.target.value.trim()
                              ? { ...(x.replyConfig ?? {}), mn: e.target.value }
                              : null,
                          }
                        : null,
                    )
                  }
                  className="w-full rounded border px-3 py-2 text-sm"
                  rows={2}
                  placeholder="Empty = use platform default reply"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Block ref (channel-runtime block id — beats reply)
                </label>
                <input
                  value={editingBotCfg.blockRef ?? ''}
                  onChange={(e) =>
                    setEditingBotCfg((x) => (x ? { ...x, blockRef: e.target.value } : null))
                  }
                  className="w-full rounded border px-3 py-2 text-sm font-mono"
                  placeholder="block_7598_…"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Workflow id (uuid)</label>
                <input
                  value={editingBotCfg.workflowId ?? ''}
                  onChange={(e) =>
                    setEditingBotCfg((x) => (x ? { ...x, workflowId: e.target.value } : null))
                  }
                  className="w-full rounded border px-3 py-2 text-sm font-mono"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={handleSaveBotCfg} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded">
                  Save
                </button>
                <button onClick={() => setEditingBotCfg(null)} className="px-3 py-1.5 text-sm bg-gray-200 rounded">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {editing && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg max-w-lg w-full space-y-3 max-h-[90vh] overflow-y-auto">
              <h3 className="font-semibold">Edit {editing.code}</h3>
              <input
                placeholder="Name"
                value={editing.name}
                onChange={(e) =>
                  setEditing((x) => (x ? { ...x, name: e.target.value } : null))
                }
                className="w-full rounded border px-3 py-2 text-sm"
              />
              <textarea
                placeholder="Description (shown to the LLM verifier as context)"
                value={editing.description}
                onChange={(e) =>
                  setEditing((x) => (x ? { ...x, description: e.target.value } : null))
                }
                className="w-full rounded border px-3 py-2 text-sm"
                rows={2}
              />
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Trigger patterns (one per line)</label>
                <p className="text-xs text-gray-400 mb-1">{FIELD_HELP.patterns}</p>
                <textarea
                  value={(editing.triggerPatterns || []).join('\n')}
                  onChange={(e) =>
                    setEditing((x) => (x ? { ...x, triggerPatterns: splitLines(e.target.value) } : null))
                  }
                  className="w-full rounded border px-3 py-2 text-sm font-mono"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Example phrases (one per line) — {(editing.examplePhrases ?? []).length} phrases
                </label>
                <p className="text-xs text-gray-400 mb-1">{FIELD_HELP.phrases}</p>
                <textarea
                  value={(editing.examplePhrases || []).join('\n')}
                  onChange={(e) =>
                    setEditing((x) => (x ? { ...x, examplePhrases: splitLines(e.target.value) } : null))
                  }
                  className="w-full rounded border px-3 py-2 text-sm"
                  rows={8}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Default reply (Монгол) — platform-wide; bots may override per-bot
                </label>
                <p className="text-xs text-gray-400 mb-1">
                  Sent on every bot when this intent fires, unless the bot configures its own reply/flow. Empty = no reply (falls through to RAG).
                </p>
                <textarea
                  value={editing.defaultReply?.mn ?? ''}
                  onChange={(e) =>
                    setEditing((x) =>
                      x
                        ? {
                            ...x,
                            defaultReply: e.target.value.trim()
                              ? { ...(x.defaultReply ?? {}), mn: e.target.value }
                              : undefined,
                          }
                        : null,
                    )
                  }
                  className="w-full rounded border px-3 py-2 text-sm"
                  rows={2}
                  placeholder="Сайн байна уу! Танд юугаар туслах вэ?"
                />
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editing.isActive !== false}
                  onChange={(e) =>
                    setEditing((x) => (x ? { ...x, isActive: e.target.checked } : null))
                  }
                />
                Active
              </label>
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => handleUpdate(editing.code)}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded"
                >
                  Save
                </button>
                <button
                  onClick={() => setEditing(null)}
                  className="px-3 py-1.5 text-sm bg-gray-200 rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AiConfigTabShell>
  );
}
